import { z } from 'zod';

import {
  createEntityAdapterSchema,
  findDuplicateIds,
  findDuplicateNames,
} from '../../base/primitives';
import { FunctionSchema } from './function.schema';

/**
 * Normalized functions collection using entity adapter pattern
 * Stores all project functions in normalized state
 */
export const FunctionsCollectionSchema = z
  .object({
    functions: createEntityAdapterSchema(FunctionSchema),
  })
  .superRefine((data, ctx) => {
    const fns = Object.values(data.functions.entities).filter(
      fn => !fn.archived,
    );

    // Check for duplicate function names
    const duplicateNames = findDuplicateNames(fns);
    for (const duplicate of duplicateNames) {
      ctx.addIssue({
        path: ['functions', 'entities', duplicate.id, 'name'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate name',
      });
    }

    // Check for duplicate function IDs
    const duplicateIds = findDuplicateIds(fns);
    for (const duplicate of duplicateIds) {
      ctx.addIssue({
        path: ['functions', 'entities', duplicate.id, 'id'],
        code: z.ZodIssueCode.custom,
        message: 'Duplicate ID',
      });
    }

    // Check for duplicate parameter names and IDs within each function
    for (const fn of fns) {
      const parameters = Object.values(fn.parameters.entities);

      const duplicateParameterNames = findDuplicateNames(parameters);
      for (const duplicate of duplicateParameterNames) {
        ctx.addIssue({
          path: [
            'functions',
            'entities',
            fn.id,
            'parameters',
            'entities',
            duplicate.id,
            'name',
          ],
          code: z.ZodIssueCode.custom,
          message: 'Duplicate parameter name',
        });
      }

      const duplicateParameterIds = findDuplicateIds(parameters);
      for (const duplicate of duplicateParameterIds) {
        ctx.addIssue({
          path: [
            'functions',
            'entities',
            fn.id,
            'parameters',
            'entities',
            duplicate.id,
            'id',
          ],
          code: z.ZodIssueCode.custom,
          message: 'Duplicate parameter ID',
        });
      }
    }
  })
  .describe(
    'Functions domain - Custom code execution for business logic and integrations. ' +
      'Functions allow running Python code during conversations for complex logic, API calls, calculations, and data transformations. ' +
      'They can read and write variables, make external API calls, and return results to flows. ' +
      'Relationships: References variables (for reading/writing data), API integrations (for external calls), ' +
      'and entities (for data validation). Referenced by flows (for execution). ' +
      "Use the 'functions' domain in MCP to see function signatures, parameters, and execution patterns.",
  );

export type FunctionsCollection = z.infer<typeof FunctionsCollectionSchema>;
